import { createEntityAdapter, createSlice } from "@reduxjs/toolkit";
import { PUBLISH_FILE_TYPE_TO_STATE_KEY } from "constants/v2/publishConstants";
import { v4 as uuidv4 } from "uuid";

import {
  distinctListPublishes,
  getDailyMotionCategories,
  getDailyMotionLanguages,
  getDailyMotionPlaylists,
  getDailyMotionProfiles,
  getDropboxAccountsList,
  getDropboxTeamFoldersList,
  getFrameIOAccounts,
  getFrameIOAssets,
  getFrameIOCredentialsList,
  getFrameIOProjects,
  getFrameIOTeams,
  getGoogleDriveAccountsList,
  getGoogleDriveFoldersList,
  getPublishFiles,
  getShadeAccountsList,
  getSlackChannelsList,
  getSlackUsersList,
  getSlackWorkspaceList,
  listPublishes,
} from "./actions";

export const publishEntityAdapter = createEntityAdapter({
  selectId: (entity) => entity.id,
});

const initialState = publishEntityAdapter.getInitialState({
  count: 0,
  isLoading: false,
  isInitialLoading: false,
  currentPage: 1,
  hasMore: true,
  publishHistoryIds: [],
  slackWorkspaces: [],
  connectedSlackChannelsList: [],
  connectedSlackPeopleList: [],
  dropboxAccountsList: [],
  dropboxTeamFoldersList: [],
  emailPublishEventIds: [],
  brightcovePublishIds: [],
  publishFiles: { default: [], custom: [] },
  ftpPublishFiles: { default: [], custom: [] },
  frameIOPublishFiles: { default: [], custom: [] },
  integrationPublishFiles: { default: [], custom: [] },
  modals: {},
  // DailyMotion state
  dailyMotionProfiles: [],
  dailyMotionCategories: [],
  dailyMotionLanguages: [],
  dailyMotionPlaylists: [],
  dailyMotionLoading: {
    profiles: false,
    categories: false,
    languages: false,
    playlists: false,
  },
  // Google Drive state
  googleDriveCredentials: {},
  googleDriveFolders: [],
  // Frame.io state
  frameIOCredentials: [],
  frameIOAccounts: [],
  frameIOTeams: [],
  frameIOProjects: [],
  frameIOAssets: [],
  frameIOLoading: {
    accounts: false,
    teams: false,
    projects: false,
    assets: false,
  },
  // Shade state
  shadeAccounts: [],
  shadeAccountsLoading: false,
});

export const publishSlice = createSlice({
  name: "publish",
  initialState: initialState,
  reducers: {
    showCloudPublishPreviewModal: (state, action) => {
      state.modals.cloudPublishPreviewModal = action.payload;
    },
    showFrameIOPublishPreviewModal: (state, action) => {
      state.modals.frameIOPublishPreviewModal = action.payload;
    },
    showGoogleDrivePublishPreviewModal: (state, action) => {
      state.modals.googleDrivePublishPreviewModal = action.payload;
    },
    storeEmailPublishEventId: (state, action) => {
      state.emailPublishEventIds.push(action.payload);
    },
    removeEmailPublishEventId: (state, action) => {
      state.emailPublishEventIds = state.emailPublishEventIds.filter((eventId) => eventId !== action.payload);
    },
    storeBrightcovePublishId: (state, action) => {
      state.brightcovePublishIds.push(action.payload);
    },
    removeBrightcovePublishId: (state, action) => {
      state.brightcovePublishIds = state.brightcovePublishIds.filter((publishId) => publishId !== action.payload);
    },
    addBulkPublishItems: (state, action) => {
      const publishedClips = action.payload?.data || [];
      const publishItems = publishedClips.flatMap((clip) => {
        return clip.clipPublished.map((p) => {
          return {
            ...p,
            id: uuidv4(),
            contentId: clip._id,
          };
        });
      });

      publishEntityAdapter.setAll(state, publishItems);
    },
    upsertPublishItem: (state, action) => {
      const { publishId, data } = action.payload;

      if (publishId && data) {
        // Find the item in the items array and prepare it for upsert
        publishEntityAdapter.upsertOne(state, {
          ...data,
          id: publishId,
        });
      }
    },
    resetPublishHistoryPagination: (state) => {
      state.publishHistoryIds = [];
      state.currentPage = 1;
      state.hasMore = true;
      state.isLoading = false;
      state.isInitialLoading = false;
    },
    clearPublishEntities: (state) => {
      publishEntityAdapter.removeAll(state);
      state.count = 0;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(listPublishes.pending, (state, action) => {
      if (action.meta.arg?.fromPublishTab) {
        if (action.meta.arg?.append) {
          // Loading more for publish tab
          state.isLoading = true;
        } else {
          // Initial loading for publish tab
          state.isInitialLoading = true;
          state.currentPage = 1;
          state.hasMore = true;
        }
      } else if (!action.meta.arg?.append) {
        state.isLoading = true;
      }
    });
    builder.addCase(listPublishes.fulfilled, (state, action) => {
      if (!action.payload.error) {
        if (action.meta.arg?.fromPublishTab) {
          const publishIds = action.payload?.data?.items?.map((item) => item.id) || [];
          const totalItems = action.payload?.data?.totalItems || 0;
          const pageSize = action.meta.arg?.payload?.pageSize || 20;
          const currentPage = action.meta.arg?.payload?.page || 1;

          if (action.meta.arg?.append) {
            // For infinite scroll, append new IDs to existing ones
            const uniqueIds = [...new Set([...state.publishHistoryIds, ...publishIds])];

            state.publishHistoryIds = uniqueIds;
          } else {
            // For initial load, replace all IDs
            state.publishHistoryIds = publishIds;
          }

          // Update pagination state
          state.currentPage = currentPage;
          const totalPages = Math.ceil(totalItems / pageSize);

          state.hasMore = currentPage < totalPages && publishIds.length > 0;

          publishEntityAdapter.upsertMany(state, action.payload?.data?.items || []);
        } else if (action.payload.data?.currentPage === 1 && !action.meta.arg?.append) {
          publishEntityAdapter.setAll(state, action.payload.data.items);
        } else {
          if (action.meta.arg?.resetList) {
            publishEntityAdapter.setAll(state, action.payload.data.items);
          } else {
            publishEntityAdapter.upsertMany(state, action.payload.data.items);
          }
        }
        if (!action.meta.arg?.append && !action.meta.arg?.fromPublishTab) {
          state.count = action.payload.data.totalItems;
        }
      } else {
        publishEntityAdapter.setAll(state, []);
        if (action.meta.arg?.fromPublishTab) {
          state.hasMore = false;
        }
      }
      state.isLoading = false;
      state.isInitialLoading = false;
    });
    builder.addCase(listPublishes.rejected, (state, action) => {
      state.isLoading = false;
      state.isInitialLoading = false;
      if (action.meta.arg?.fromPublishTab) {
        state.hasMore = false;
      }
    });

    builder.addCase(distinctListPublishes.fulfilled, (state, action) => {
      if (!action.payload.error) {
        if (action.meta.arg?.append) {
          publishEntityAdapter.upsertMany(state, action.payload.data.items);
        } else {
          publishEntityAdapter.setAll(state, action.payload.data.items);
        }
      }
    });
    builder.addCase(getSlackWorkspaceList.fulfilled, (state, action) => {
      if (action?.payload?.statusCode === 200 && action?.payload?.data?.workspaces?.length > 0) {
        state.slackWorkspaces = action.payload.data.workspaces;
      } else {
        state.slackWorkspaces = [];
      }
    });
    builder.addCase(getSlackChannelsList.fulfilled, (state, action) => {
      if (action?.payload?.statusCode === 200) {
        const newChannels = action?.payload?.data?.channels || [];
        const existingChannels = state.connectedSlackChannelsList || [];

        const existingIds = new Set(existingChannels.map((channel) => channel?.id).filter(Boolean));

        const channelsToAdd = [];

        newChannels.forEach((channel) => {
          if (channel?.id && !existingIds.has(channel.id)) {
            existingIds.add(channel.id);
            channelsToAdd.push(channel);
          }
        });

        if (channelsToAdd.length > 0) {
          state.connectedSlackChannelsList = existingChannels.concat(channelsToAdd);
        }
      }
    });
    builder.addCase(getSlackUsersList.fulfilled, (state, action) => {
      if (action?.payload?.statusCode === 200) {
        state.connectedSlackPeopleList = action?.payload?.data?.people;
      } else {
        state.connectedSlackPeopleList = [];
      }
    });
    builder.addCase(getDropboxAccountsList.fulfilled, (state, action) => {
      if (action?.payload?.statusCode === 200) {
        state.dropboxAccountsList = action?.payload?.data?.credentials;
      } else {
        state.dropboxAccountsList = [];
      }
    });
    builder.addCase(getDropboxTeamFoldersList.fulfilled, (state, action) => {
      if (action?.payload?.statusCode === 200) {
        state.dropboxTeamFoldersList = action?.payload?.data?.folders?.teamFolders;
      } else {
        state.dropboxTeamFoldersList = [];
      }
    });
    builder.addCase(getPublishFiles.fulfilled, (state, action) => {
      const publishType = action.meta.arg.payload.publishType;

      const stateKey = PUBLISH_FILE_TYPE_TO_STATE_KEY[publishType] || "publishFiles";

      if (action?.payload?.statusCode === 200) {
        state[stateKey] = action.payload.data;
      } else {
        state[stateKey] = { default: [], custom: [] };
      }
    });
    // builder.addCase(cloudPublish.fulfilled, (state, action) => {
    //   const payload = action.meta.arg.payload;

    //   const contentId = payload?.contentId;
    //   const platform = payload?.bucket?.name;
    //   const contentType = payload?.contentType;

    //   state.clipsIds = [...new Set([...state.clipsIds, contentId])];

    //   if (!state.clipPublishEntities[contentId]) {
    //     state.clipPublishEntities[contentId] = [{ platform, contentType, type: "cloud", status: "completed" }];
    //   } else {
    //     state.clipPublishEntities[contentId] = [
    //       ...state.clipPublishEntities[contentId],
    //       { platform, contentType, type: "cloud", status: "completed" },
    //     ];̦
    //   }
    // });

    // DailyMotion extraReducers
    builder.addCase(getDailyMotionProfiles.pending, (state) => {
      state.dailyMotionLoading.profiles = true;
    });
    builder.addCase(getDailyMotionProfiles.fulfilled, (state, action) => {
      state.dailyMotionLoading.profiles = false;
      if (action?.payload?.statusCode === 200 && action?.payload?.data) {
        state.dailyMotionProfiles = action.payload.data;
      } else {
        state.dailyMotionProfiles = [];
      }
    });
    builder.addCase(getDailyMotionProfiles.rejected, (state) => {
      state.dailyMotionLoading.profiles = false;
    });

    builder.addCase(getDailyMotionCategories.pending, (state) => {
      state.dailyMotionLoading.categories = true;
    });
    builder.addCase(getDailyMotionCategories.fulfilled, (state, action) => {
      state.dailyMotionLoading.categories = false;
      if (action?.payload?.statusCode === 200 && action?.payload?.data) {
        state.dailyMotionCategories = action.payload.data;
      } else {
        state.dailyMotionCategories = [];
      }
    });
    builder.addCase(getDailyMotionCategories.rejected, (state) => {
      state.dailyMotionLoading.categories = false;
    });

    builder.addCase(getDailyMotionLanguages.pending, (state) => {
      state.dailyMotionLoading.languages = true;
    });
    builder.addCase(getDailyMotionLanguages.fulfilled, (state, action) => {
      state.dailyMotionLoading.languages = false;
      if (action?.payload?.statusCode === 200 && action?.payload?.data) {
        state.dailyMotionLanguages = action.payload.data;
      } else {
        state.dailyMotionLanguages = [];
      }
    });
    builder.addCase(getDailyMotionLanguages.rejected, (state) => {
      state.dailyMotionLoading.languages = false;
    });

    builder.addCase(getDailyMotionPlaylists.pending, (state) => {
      state.dailyMotionLoading.playlists = true;
    });
    builder.addCase(getDailyMotionPlaylists.fulfilled, (state, action) => {
      state.dailyMotionLoading.playlists = false;
      if (action?.payload?.statusCode === 200 && action?.payload?.data) {
        state.dailyMotionPlaylists = action.payload.data;
      } else {
        state.dailyMotionPlaylists = [];
      }
    });
    builder.addCase(getDailyMotionPlaylists.rejected, (state) => {
      state.dailyMotionLoading.playlists = false;
    });

    // Google Drive extraReducers
    builder.addCase(getGoogleDriveAccountsList.fulfilled, (state, action) => {
      if (action?.payload?.statusCode === 200 && action?.payload?.data) {
        state.googleDriveCredentials = action.payload.data;
      } else {
        state.googleDriveCredentials = {};
      }
    });

    // Frame.io extraReducers
    builder.addCase(getFrameIOCredentialsList.fulfilled, (state, action) => {
      console.log("action", action);
      if (action?.payload?.statusCode === 200 && action?.payload?.data) {
        state.frameIOCredentials = action.payload.data;
      } else {
        state.frameIOCredentials = [];
      }
    });

    // Frame.io Accounts
    builder.addCase(getFrameIOAccounts.pending, (state) => {
      state.frameIOLoading.accounts = true;
    });
    builder.addCase(getFrameIOAccounts.fulfilled, (state, action) => {
      state.frameIOLoading.accounts = false;
      if (action?.payload?.statusCode === 200 && action?.payload?.data) {
        state.frameIOAccounts = action.payload?.data?.accounts || [];
      } else {
        state.frameIOAccounts = [];
      }
    });
    builder.addCase(getFrameIOAccounts.rejected, (state) => {
      state.frameIOLoading.accounts = false;
      state.frameIOAccounts = [];
    });

    // Frame.io Teams
    builder.addCase(getFrameIOTeams.pending, (state) => {
      state.frameIOLoading.teams = true;
    });
    builder.addCase(getFrameIOTeams.fulfilled, (state, action) => {
      state.frameIOLoading.teams = false;
      if (action?.payload?.statusCode === 200 && action?.payload?.data) {
        state.frameIOTeams = action.payload.data?.teams || [];
      } else {
        state.frameIOTeams = [];
      }
    });
    builder.addCase(getFrameIOTeams.rejected, (state) => {
      state.frameIOLoading.teams = false;
      state.frameIOTeams = [];
    });

    // Frame.io Projects
    builder.addCase(getFrameIOProjects.pending, (state) => {
      state.frameIOLoading.projects = true;
    });
    builder.addCase(getFrameIOProjects.fulfilled, (state, action) => {
      state.frameIOLoading.projects = false;
      if (action?.payload?.statusCode === 200 && action?.payload?.data) {
        state.frameIOProjects = action.payload.data?.projects || [];
      } else {
        state.frameIOProjects = [];
      }
    });
    builder.addCase(getFrameIOProjects.rejected, (state) => {
      state.frameIOLoading.projects = false;
      state.frameIOProjects = [];
    });

    // Frame.io Assets
    builder.addCase(getFrameIOAssets.pending, (state) => {
      state.frameIOLoading.assets = true;
    });
    builder.addCase(getFrameIOAssets.fulfilled, (state, action) => {
      state.frameIOLoading.assets = false;
      if (action?.payload?.statusCode === 200 && action?.payload?.data) {
        state.frameIOAssets = action.payload.data?.assets || [];
      } else {
        state.frameIOAssets = [];
      }
    });
    builder.addCase(getFrameIOAssets.rejected, (state) => {
      state.frameIOLoading.assets = false;
      state.frameIOAssets = [];
    });

    builder.addCase(getGoogleDriveFoldersList.fulfilled, (state, action) => {
      if (action?.payload?.statusCode === 200 && action?.payload?.data) {
        state.googleDriveFolders = action.payload.data;
      } else {
        state.googleDriveFolders = [];
      }
    });

    // Shade Accounts
    builder.addCase(getShadeAccountsList.pending, (state) => {
      state.shadeAccountsLoading = true;
    });
    builder.addCase(getShadeAccountsList.fulfilled, (state, action) => {
      state.shadeAccountsLoading = false;
      if (action?.payload?.statusCode === 200 && action?.payload?.data) {
        state.shadeAccounts = action.payload.data || [];
      } else {
        state.shadeAccounts = [];
      }
    });
    builder.addCase(getShadeAccountsList.rejected, (state) => {
      state.shadeAccountsLoading = false;
      state.shadeAccounts = [];
    });
  },
});

export const {
  showCloudPublishPreviewModal,
  showFrameIOPublishPreviewModal,
  showGoogleDrivePublishPreviewModal,
  addBulkPublishItems,
  upsertPublishItem,
  storeEmailPublishEventId,
  removeEmailPublishEventId,
  storeBrightcovePublishId,
  removeBrightcovePublishId,
  resetPublishHistoryPagination,
  clearPublishEntities,
} = publishSlice.actions;

export default publishSlice.reducer;
